import { Icon, type IconName } from "@openanalyst-ai/ui/icon"
import { Tooltip } from "@openanalyst-ai/ui/tooltip"
import { For, Show, type JSX } from "solid-js"
import { useLanguage } from "@/context/language"
import { BRAND_ICONS, type BrandIcon } from "@/plugins/brand-icons"
import {
  defaultProjectTemplateStarterPanel,
  type ProjectStarterTemplateID,
  type StarterPanelItem,
} from "@/services/project-starters"

/**
 * Single-page workspace picker.
 *
 * Layout per card:
 *   ┌─────────────────────────────────────┐
 *   │  [starter icon tile]                │
 *   ├─────────────────────────────────────┤
 *   │  Title                              │
 *   │  Description …                      │
 *   │                                     │
 *   │  [Create →]           [🛍][📢][📊] │
 *   └─────────────────────────────────────┘
 *
 * Integration icons on the right of the footer use the real brand SVGs
 * from `BRAND_ICONS` (simple-icons). They sit small at rest; on hover
 * each pops up (scale + lift) with a tooltip showing the brand name.
 *
 * The custom-project tile stays first so users always see "open existing
 * folder" alongside the starters.
 */

const integrationsByTemplate: Record<ProjectStarterTemplateID, string[]> = {
  "ai-marketing-workspace": ["ga", "gdocs", "gsheets"],
  "email-revenue-system": ["gmail", "hubspot", "stripe", "shopify"],
  "ecommerce-ads-analysis": ["shopify", "ga", "meta_ads", "gads"],
  "google-ads-growth-review": ["gads", "ga", "gsheets"],
  "facebook-ads-performance-review": ["meta_ads", "facebook", "ga"],
  "paid-ads-creative-strategy": ["meta_ads", "gads", "drive"],
  "funnel-conversion-analysis": ["ga", "stripe", "hubspot"],
  "lead-generation-system": ["hubspot", "gmail", "gsheets"],
  "seo-authority-growth-system": ["ga", "gdocs", "gsheets"],
  "youtube-social-growth-system": ["youtube", "drive", "gdocs"],
  "analytics-measurement-readiness": ["ga", "gsheets", "bigquery"],
  "openanalyst-shopify-manager": ["shopify", "ga", "stripe"],
  "job-research": ["linkedin", "gsheets", "gdocs"],
}

const starterIllustrationsByTemplate: Record<
  ProjectStarterTemplateID,
  {
    label: string
    icon: IconName
    primary: string
    surface: string
  }
> = {
  "ai-marketing-workspace": {
    label: "AI marketing workspace starter illustration",
    icon: "sparkles",
    primary: "#6d4fc2",
    surface: "#f4f0ff",
  },
  "email-revenue-system": {
    label: "Email automation starter illustration",
    icon: "receipt-text",
    primary: "#17854c",
    surface: "#edf7f0",
  },
  "openanalyst-shopify-manager": {
    label: "Shopify revenue starter illustration",
    icon: "shopify",
    primary: "#157a54",
    surface: "#edf6f1",
  },
  "ecommerce-ads-analysis": {
    label: "Ecommerce ads starter illustration",
    icon: "storefront",
    primary: "#2563eb",
    surface: "#eef4ff",
  },
  "google-ads-growth-review": {
    label: "Google Ads growth starter illustration",
    icon: "ads",
    primary: "#2457c5",
    surface: "#f0f5ff",
  },
  "facebook-ads-performance-review": {
    label: "Paid social performance starter illustration",
    icon: "meta-ads",
    primary: "#275fca",
    surface: "#eef3fb",
  },
  "paid-ads-creative-strategy": {
    label: "Creative testing starter illustration",
    icon: "photo",
    primary: "#b45309",
    surface: "#fff7ed",
  },
  "funnel-conversion-analysis": {
    label: "Conversion funnel starter illustration",
    icon: "activity",
    primary: "#0f766e",
    surface: "#eef8f6",
  },
  "lead-generation-system": {
    label: "Lead generation starter illustration",
    icon: "users",
    primary: "#0f766e",
    surface: "#eff8f7",
  },
  "seo-authority-growth-system": {
    label: "SEO search starter illustration",
    icon: "magnifying-glass",
    primary: "#2f6f3e",
    surface: "#f2f7ee",
  },
  "youtube-social-growth-system": {
    label: "YouTube growth starter illustration",
    icon: "panels-top-left",
    primary: "#c2410c",
    surface: "#fff3f2",
  },
  "analytics-measurement-readiness": {
    label: "Analytics measurement starter illustration",
    icon: "analytics",
    primary: "#4f46b5",
    surface: "#f3f2fb",
  },
  "job-research": {
    label: "Job research starter illustration",
    icon: "magnifying-glass",
    primary: "#0f766e",
    surface: "#eef8f6",
  },
}

// Map our "integration id" (e.g. "ga", "gads", "meta_ads") to a brand id
// in BRAND_ICONS. Anything not in this map falls back to a colored letter chip.
const BRAND_ALIAS: Record<string, string> = {
  gmail: "gmail",
  drive: "googledrive",
  gdocs: "googledocs",
  gsheets: "googlesheets",
  ga: "googleanalytics",
  gads: "googleads",
  bigquery: "googlebigquery",
  youtube: "youtube",
  facebook: "facebook",
  meta_ads: "meta",
  hubspot: "hubspot",
  stripe: "stripe",
  shopify: "shopify",
}

function brandFor(integrationId: string): BrandIcon | undefined {
  const brandId = BRAND_ALIAS[integrationId] ?? integrationId
  return BRAND_ICONS.find((b) => b.id === brandId)
}

function IntegrationIcon(props: { id: string; index: number }) {
  const brand = brandFor(props.id)
  return (
    <Tooltip placement="top" value={brand?.title ?? props.id}>
      <span
        data-workspace-integ-chip=""
        style={{
          "background-color": brand?.hex ?? "var(--oa-paper-2)",
          "--chip-stagger": `${props.index * 30}ms`,
        }}
        aria-label={brand?.title ?? props.id}
      >
        <Show
          when={brand}
          keyed
          fallback={<span data-workspace-integ-chip-fallback="">{props.id.charAt(0).toUpperCase()}</span>}
        >
          {(b) => (
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              data-workspace-integ-chip-svg=""
              aria-hidden="true"
            >
              <path d={b.path} fill="currentColor" />
            </svg>
          )}
        </Show>
      </span>
    </Tooltip>
  )
}

export function StarterIllustration(props: { templateId: ProjectStarterTemplateID }) {
  const illustration = starterIllustrationsByTemplate[props.templateId]
  return (
    <div
      data-workspace-card-v3-illustration=""
      data-workspace-card-v3-illustration-icon={illustration.icon}
      role="img"
      aria-label={illustration.label}
      style={{
        "--starter-primary": illustration.primary,
        "--starter-surface": illustration.surface,
      }}
    >
      <span data-workspace-ill-bg="" />
      <span data-workspace-ill-icon-shell="">
        <Icon name={illustration.icon} size="large" />
      </span>
    </div>
  )
}

export function WorkspaceSelector(props: {
  onCustomProject?: () => Promise<void> | void
  onStarterProject: (templateId: ProjectStarterTemplateID) => Promise<void> | void
  starterProjectRoot?: string
  onChangeStarterProjectRoot?: () => Promise<void> | void
  recommendedTemplateId?: ProjectStarterTemplateID
  eyebrow?: JSX.Element
  headline?: JSX.Element
  subhead?: JSX.Element
  disabled?: boolean
}): JSX.Element {
  const language = useLanguage()
  const items = defaultProjectTemplateStarterPanel()
    .sections.flatMap((s) => s.items)
    .filter(
      (item): item is StarterPanelItem & { action: { kind: "createProject"; templateId: ProjectStarterTemplateID } } =>
        item.action.kind === "createProject",
    )
  const subhead = () =>
    props.subhead ??
    (props.onCustomProject
      ? "Pick a ready workspace for a common workflow, or open any existing local folder."
      : "Pick the ready workspace closest to what you want to build first. OpenAnalyst will create the local project with the right structure in place.")

  return (
    <div data-workspace-selector="">
      <div data-workspace-top-row="">
        <span data-workspace-eyebrow="">{props.eyebrow ?? "New project"}</span>

        <div data-workspace-title-row="">
          <h2 data-workspace-headline="">
            {props.headline ?? ["Choose a ", <span data-workspace-headline-accent="">project starter</span>]}
          </h2>

          <Show when={props.starterProjectRoot || props.onChangeStarterProjectRoot}>
            <button
              type="button"
              data-workspace-loc-fancy=""
              onClick={() => void props.onChangeStarterProjectRoot?.()}
              disabled={props.disabled || !props.onChangeStarterProjectRoot}
              title={
                props.onChangeStarterProjectRoot ? language.t("project.start.root.change") : props.starterProjectRoot
              }
            >
              <span data-workspace-loc-fancy-label-row="">
                <Icon name="folder-add-left" size="small" />
                <span data-workspace-loc-fancy-label="">{language.t("project.start.root.label")}</span>
              </span>
              <span data-workspace-loc-fancy-path-row="">
                <span data-workspace-loc-fancy-path="" title={props.starterProjectRoot}>
                  {props.starterProjectRoot || language.t("project.start.root.default")}
                </span>
                <Show when={props.onChangeStarterProjectRoot}>
                  <Icon name="pencil-line" size="small" />
                </Show>
              </span>
            </button>
          </Show>
        </div>
        <p data-workspace-subhead="">{subhead()}</p>
      </div>

      <div data-workspace-scroll="">
        <div data-workspace-grid-v3="">
          {props.onCustomProject ? (
            <button
              type="button"
              data-workspace-card-v3=""
              data-workspace-card-v3-custom=""
              style={{ "animation-delay": "0ms" }}
              onClick={() => void props.onCustomProject?.()}
              disabled={props.disabled}
            >
              <div data-workspace-card-v3-thumb="" data-workspace-card-v3-thumb-custom="">
                <Icon name="folder-add-left" size="large" />
              </div>
              <div data-workspace-card-v3-overlay="">
                <span data-workspace-card-v3-title="">Custom project</span>
                <span data-workspace-card-v3-desc-line="">Open an existing folder from your computer.</span>
                <div data-workspace-card-v3-foot="">
                  <span data-workspace-card-v3-cta="">
                    Browse folder <Icon name="arrow-right" size="small" />
                  </span>
                </div>
              </div>
            </button>
          ) : null}

          <For each={items}>
            {(item, index) => {
              const templateId = item.action.templateId
              const illustration = starterIllustrationsByTemplate[templateId]
              const isPriorityTag =
                item.detail === "Best first" || item.detail === "Revenue workflow" || item.detail === "Growth audit"
              const isRecommended = templateId === (props.recommendedTemplateId ?? "ai-marketing-workspace")
              const integrations = (integrationsByTemplate[templateId] ?? []).slice(0, 3)
              return (
                <button
                  type="button"
                  data-workspace-card-v3=""
                  data-workspace-card-v3-recommended={isRecommended ? "" : undefined}
                  style={{
                    "animation-delay": `${(index() + (props.onCustomProject ? 1 : 0)) * 40}ms`,
                    "--starter-primary": illustration.primary,
                    "--starter-surface": illustration.surface,
                  }}
                  onClick={() => void props.onStarterProject(templateId)}
                  disabled={props.disabled}
                >
                  <Show when={isRecommended || item.detail}>
                    <span data-workspace-card-v3-tags="">
                      <Show when={item.detail}>
                        <span
                          data-workspace-card-v3-tier=""
                          data-workspace-card-v3-tier-priority={isPriorityTag ? "" : undefined}
                        >
                          {item.detail}
                        </span>
                      </Show>
                      <Show when={isRecommended}>
                        <span data-workspace-card-v3-badge="">Recommended</span>
                      </Show>
                    </span>
                  </Show>
                  <div data-workspace-card-v3-thumb="">
                    <StarterIllustration templateId={templateId} />
                  </div>
                  <div data-workspace-card-v3-overlay="">
                    <span data-workspace-card-v3-title="">{item.title}</span>
                    <Show when={item.description}>
                      <span data-workspace-card-v3-desc-line="">{item.description}</span>
                    </Show>
                    <div data-workspace-card-v3-foot="">
                      <Show when={integrations.length > 0}>
                        <span data-workspace-integs-v2="">
                          <For each={integrations}>{(id, i) => <IntegrationIcon id={id} index={i()} />}</For>
                        </span>
                      </Show>
                      <span data-workspace-card-v3-cta="">
                        Create <Icon name="arrow-right" size="small" />
                      </span>
                    </div>
                  </div>
                </button>
              )
            }}
          </For>
        </div>
      </div>
    </div>
  )
}
